import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Globe, ArrowRight, ChartLineUp, UsersThree, Target, TrendUp,
  Sparkle, CursorClick, Storefront, MapPin, CircleNotch,
} from "@phosphor-icons/react";

function fmtNum(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtRate(v) {
  return v === null || v === undefined ? "—" : `${v}%`;
}

export default function WebsiteAnalytics() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [websites, setWebsites] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [websiteId, setWebsiteId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [days, setDays] = useState(30);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (websiteId) params.website_id = websiteId;
      if (campaignId) params.campaign_id = campaignId;
      const [wa, ws, cs] = await Promise.all([
        api.get("/marketing/website-analytics", { params }),
        websites.length === 0 ? api.get("/websites").catch(() => ({ data: [] })) : Promise.resolve({ data: websites }),
        campaigns.length === 0 ? api.get("/campaigns").catch(() => ({ data: [] })) : Promise.resolve({ data: campaigns }),
      ]);
      setData(wa.data);
      if (websites.length === 0) setWebsites(ws.data || []);
      if (campaigns.length === 0) setCampaigns(cs.data || []);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, [days, websiteId, campaignId, websites.length, campaigns.length]);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  const runInsight = async () => {
    setInsightLoading(true);
    try {
      const params = { days };
      if (websiteId) params.website_id = websiteId;
      if (campaignId) params.campaign_id = campaignId;
      const { data: r } = await api.post("/marketing/website-analytics/insight", null, { params });
      setInsight(r);
      if (r.insufficient_data) toast.message("Not enough data yet for an AI observation");
      else toast.success("Insight generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate insight");
    } finally { setInsightLoading(false); }
  };

  const t = data?.totals;
  const funnel = data?.funnel;

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="website-analytics-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Globe weight="duotone" className="h-3 w-3" /> Marketing · Website Analytics
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Website analytics</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Rolls up GA4 rows posted via <code className="mx-1 px-1 py-0.5 rounded bg-graphite-950 text-ink-primary/80 text-[11px]">/api/inbound/website-metrics/batch</code>
            plus the CTA / assessment / lead events fired directly from the public Landing Pages.
            Empty ratios mean the denominator was zero — no divide-by-zero noise.
          </p>
        </div>
        <Link to="/marketing"
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 px-4 py-2 text-sm text-ink-primary hover:bg-graphite-900"
          data-testid="website-analytics-back">
          Marketing overview <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Filters */}
      <div className="mt-8 rounded-md border border-white/10 bg-graphite-900 p-4 flex items-center gap-3 flex-wrap"
           data-testid="website-analytics-filters">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-ink-secondary">Website</span>
          <select data-testid="wa-filter-website" value={websiteId}
            onChange={e => setWebsiteId(e.target.value)}
            className="h-8 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
            <option value="">All</option>
            {websites.map(w => <option key={w.website_id} value={w.website_id}>{w.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-ink-secondary">Campaign</span>
          <select data-testid="wa-filter-campaign" value={campaignId}
            onChange={e => setCampaignId(e.target.value)}
            className="h-8 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
            <option value="">All</option>
            {campaigns.map(c => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-ink-secondary">Days</span>
          <select data-testid="wa-filter-days" value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="h-8 rounded-md bg-graphite-950 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30">
            {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="website-analytics-loading">Loading website analytics…</div>
      ) : !data ? (
        <div className="mt-10 text-sm text-ink-secondary">Website analytics unavailable.</div>
      ) : !data.has_data ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
             data-testid="website-analytics-empty">
          <Globe weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No website analytics data yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            Once GA4 rows are posted to <code>/api/inbound/website-metrics/batch</code>
            (or your first Landing Page picks up traffic), the KPIs, traffic sources,
            landing page performance and funnel will appear here.
          </p>
          <Link to="/websites"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200">
            <Globe weight="fill" className="h-4 w-4" /> Manage websites
          </Link>
        </div>
      ) : (
        <>
          <div className="mt-8 text-[10px] uppercase tracking-[0.2em] text-ink-secondary" data-testid="wa-window">
            Last {data.days} days · {data.row_count} row{data.row_count === 1 ? "" : "s"}
          </div>

          {/* KPI cards */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="wa-kpis">
            <Stat label="Users" value={fmtNum(t.users)} Icon={UsersThree} testid="wa-kpi-users" />
            <Stat label="Sessions" value={fmtNum(t.sessions)} Icon={ChartLineUp} testid="wa-kpi-sessions" />
            <Stat label="Website leads" value={fmtNum(t.leads)} Icon={Target} tone="text-emerald-300" testid="wa-kpi-leads" />
            <Stat label="Conversion rate" value={fmtRate(t.conversion_rate)}
                  sub={t.conversion_rate === null ? "no users" : "leads ÷ users"} Icon={TrendUp} tone="text-blue-300"
                  testid="wa-kpi-conv-rate" />
            <Stat label="Top source" value={data.top_source || "—"} Icon={MapPin}
                  tone="text-fuchsia-300" testid="wa-kpi-top-source" />
            <Stat label="Top landing page" value={data.top_landing_page || "—"} Icon={Storefront}
                  tone="text-amber-300" testid="wa-kpi-top-lp" />
          </div>

          {/* Traffic sources */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6"
                   data-testid="wa-traffic-sources">
            <div className="flex items-center gap-2">
              <MapPin weight="duotone" className="h-4 w-4 text-ink-primary/80" />
              <h2 className="font-display text-lg tracking-tight">Traffic sources</h2>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-ink-secondary border-b border-white/10">
                  <tr>
                    <th className="text-left py-2 pr-4 font-medium">Source</th>
                    <th className="text-right py-2 px-3 font-medium">Users</th>
                    <th className="text-right py-2 px-3 font-medium">Sessions</th>
                    <th className="text-right py-2 px-3 font-medium">Leads</th>
                    <th className="text-right py-2 pl-3 font-medium">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {data.traffic_sources.map(row => (
                    <tr key={row.source} data-testid={`wa-source-row-${row.source}`}
                        className="border-b border-white/5 last:border-b-0">
                      <td className="py-2 pr-4 text-ink-primary">{row.source}</td>
                      <td className="py-2 px-3 text-right text-ink-primary font-mono">{fmtNum(row.users)}</td>
                      <td className="py-2 px-3 text-right text-ink-secondary font-mono">{fmtNum(row.sessions)}</td>
                      <td className="py-2 px-3 text-right text-emerald-200 font-mono">{fmtNum(row.leads)}</td>
                      <td className="py-2 pl-3 text-right text-blue-200 font-mono">{fmtRate(row.conversion_rate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Landing page performance */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6"
                   data-testid="wa-lp-performance">
            <div className="flex items-center gap-2">
              <Storefront weight="duotone" className="h-4 w-4 text-ink-primary/80" />
              <h2 className="font-display text-lg tracking-tight">Landing page performance</h2>
            </div>
            {data.landing_page_performance.length === 0 ? (
              <p className="mt-4 text-sm text-ink-secondary">No LP-level rows in this window yet.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-ink-secondary border-b border-white/10">
                    <tr>
                      <th className="text-left py-2 pr-4 font-medium">Landing page</th>
                      <th className="text-right py-2 px-3 font-medium">Visitors</th>
                      <th className="text-right py-2 px-3 font-medium">CTA clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Assessments</th>
                      <th className="text-right py-2 px-3 font-medium">Leads</th>
                      <th className="text-right py-2 pl-3 font-medium">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.landing_page_performance.map(row => (
                      <tr key={row.landing_page_id} data-testid={`wa-lp-row-${row.landing_page_id}`}
                          className="border-b border-white/5 last:border-b-0">
                        <td className="py-2 pr-4 text-ink-primary">{row.name}</td>
                        <td className="py-2 px-3 text-right text-ink-primary font-mono">{fmtNum(row.users)}</td>
                        <td className="py-2 px-3 text-right text-ink-secondary font-mono">{fmtNum(row.cta_clicks)}</td>
                        <td className="py-2 px-3 text-right text-ink-secondary font-mono">{fmtNum(row.assessment_completed)}</td>
                        <td className="py-2 px-3 text-right text-emerald-200 font-mono">{fmtNum(row.leads)}</td>
                        <td className="py-2 pl-3 text-right text-blue-200 font-mono">{fmtRate(row.conversion_rate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Funnel */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6"
                   data-testid="wa-funnel">
            <div className="flex items-center gap-2">
              <CursorClick weight="duotone" className="h-4 w-4 text-ink-primary/80" />
              <h2 className="font-display text-lg tracking-tight">Conversion funnel</h2>
            </div>
            <div className="mt-4 grid grid-cols-5 gap-3">
              {[
                { key: "visitor", label: "Visitors" },
                { key: "cta", label: "CTA click" },
                { key: "assessment", label: "Assessment" },
                { key: "lead", label: "Lead" },
                { key: "qualified", label: "Qualified" },
              ].map((step, i, arr) => {
                const v = funnel?.[step.key] || 0;
                const prev = i > 0 ? (funnel?.[arr[i - 1].key] || 0) : null;
                const rate = prev && prev > 0 ? Math.round((v / prev) * 100) : null;
                return (
                  <div key={step.key} data-testid={`wa-funnel-${step.key}`}
                       className="rounded-md border border-white/10 bg-graphite-950 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{step.label}</div>
                    <div className="mt-2 font-mono text-2xl tracking-tight text-ink-primary">{fmtNum(v)}</div>
                    {rate !== null && (
                      <div className="mt-1 text-[11px] text-ink-secondary">{rate}% of prior step</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* AI Insight */}
          <section className="mt-6 rounded-md border border-white/10 bg-graphite-900 p-6"
                   data-testid="wa-ai-insight">
            <div className="flex items-center gap-2 justify-between flex-wrap">
              <div className="flex items-center gap-2">
                <Sparkle weight="duotone" className="h-4 w-4 text-amber-300" />
                <h2 className="font-display text-lg tracking-tight">AI website insight</h2>
              </div>
              <button data-testid="wa-generate-insight" onClick={runInsight} disabled={insightLoading}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 px-4 py-2 text-sm text-ink-primary hover:bg-graphite-800 disabled:opacity-60">
                {insightLoading ? <CircleNotch weight="bold" className="h-4 w-4 animate-spin" /> : <Sparkle weight="fill" className="h-4 w-4" />}
                {insightLoading ? "Analyzing…" : "Generate insight"}
              </button>
            </div>
            {insight?.items?.length > 0 ? (
              <ul className="mt-4 space-y-2" data-testid="wa-insight-items">
                {insight.items.map((it, i) => (
                  <li key={i} data-testid={`wa-insight-item-${i}`}
                      className="rounded-md border border-white/10 bg-graphite-950 p-3 text-sm text-ink-primary leading-relaxed">
                    {it}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-sm text-ink-secondary">
                Click "Generate insight" for a plain-English summary of what stands out in this window.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, sub, Icon, tone = "text-ink-primary", testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-graphite-900 p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink-secondary">
        <Icon weight="duotone" className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-2 font-mono text-2xl tracking-tight ${tone}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-ink-secondary">{sub}</div>}
    </div>
  );
}
